import { useEffect } from "react";
import { motion } from "framer-motion";
import { Copy, ArrowRight, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { IMAGES, THANKYOU, LIVE } from "../../config/landingConfig";

export const ThankYou = () => {
  useEffect(() => {
    document.title = "Dziękuję za zakup | AI Speaking Games";
    window.fbq?.("track", "Purchase", { content_name: "AI Speaking Games", currency: "PLN" });
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(THANKYOU.code);
      toast.success("Hasło skopiowane do schowka");
    } catch {
      toast.info(`Hasło: ${THANKYOU.code}`);
    }
  };

  return (
    <div data-testid="thankyou-page" className="dot-grid flex min-h-screen flex-col bg-white">
      <header className="border-b-2 border-ink bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-3 sm:px-6">
          <a href="/" data-testid="thankyou-logo" className="flex items-center gap-2">
            <img src={IMAGES.logoMark} alt="Logo Anny Popławskiej" className="h-9 w-auto" />
            <span className="font-display text-lg uppercase tracking-wide text-ink">
              AI&nbsp;Speaking&nbsp;Games
            </span>
          </a>
        </div>
      </header>

      <main className="mx-auto grid w-full max-w-7xl flex-1 items-center gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:py-20">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-ink bg-cyan px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.16em] text-ink shadow-hard-sm">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              Zamówienie złożone
            </span>
            <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Dziękuję Ci bardzo za{" "}
              <span className="marker-cyan">dokonanie zakupu</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-ink/75 sm:text-lg">
              {LIVE.note} Szczegóły dostępu wyślemy Ci mailem.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-8 max-w-xl rounded-3xl border-2 border-ink bg-white p-6 shadow-hard sm:p-8"
          >
            <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-ink/50">
              Mam coś dla stałych klientek i klientów
            </p>
            <p className="mt-3 text-base font-semibold leading-relaxed text-ink sm:text-lg">
              Na hasło <strong className="text-magenta">{THANKYOU.code}</strong> dostaniesz{" "}
              <strong>10% rabatu</strong> na cały mój sklep.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <span
                data-testid="coupon-code"
                className="font-display rounded-2xl border-2 border-dashed border-ink bg-cyan px-8 py-4 text-4xl tracking-[0.2em] text-ink"
              >
                {THANKYOU.code}
              </span>
              <button
                data-testid="coupon-copy-button"
                onClick={copyCode}
                className="hover-lift inline-flex items-center gap-2 rounded-full border-2 border-ink bg-white px-5 py-3 text-sm font-extrabold text-ink shadow-hard-sm"
              >
                <Copy className="h-4 w-4" aria-hidden="true" />
                Kopiuj hasło
              </button>
            </div>
            <a
              href={THANKYOU.shopUrl}
              data-testid="shop-button"
              className="hover-lift mt-6 inline-flex items-center gap-2 rounded-full border-2 border-ink bg-magenta px-7 py-3.5 text-base font-extrabold text-white shadow-hard"
            >
              Przejdź do sklepu
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </motion.div>

          <motion.a
            href="/"
            data-testid="back-home-link"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-ink/60 transition-colors hover:text-magenta"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Wróć na stronę kursu
          </motion.a>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, rotate: 4 }}
          animate={{ opacity: 1, scale: 1, rotate: 2 }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-md"
        >
          <div className="float-soft absolute -left-4 -top-5 z-10 h-12 w-12 -rotate-12 rounded-2xl border-2 border-ink bg-magenta shadow-hard-sm" aria-hidden="true" />
          <div className="overflow-hidden rounded-[2rem] border-4 border-ink bg-white shadow-hard-lg">
            <img
              src="/images/anna-wave.webp"
              alt="Anna Popławska dziękuje za zakup kursu"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="float-soft absolute -bottom-5 -right-3 z-10 h-10 w-10 rotate-12 rounded-xl border-2 border-ink bg-cyan shadow-hard-sm" aria-hidden="true" />
        </motion.div>
      </main>

      <footer className="border-t-2 border-ink/10 py-6">
        <p className="text-center text-xs font-semibold text-ink/50">
          © {new Date().getFullYear()} Anna Popławska. Wszelkie prawa zastrzeżone.
        </p>
      </footer>
    </div>
  );
};
